import {
  createLocalAudioTrack,
  createLocalTracks,
  CreateLocalTracksOptions,
  createLocalVideoTrack,
  LocalTrack,
  MediaDeviceFailure,
  Track,
} from "livekit-client";
import {
  cameraDeviceError,
  cameraTrack,
  microDeviceError,
  microTrack,
  microTrackForProcessing,
  realCameraDevice,
  realMicroDevice,
} from "./state";
import { mediaActions } from "./actions";
import { applyBlurProcessor } from "./videoBlur";
import {
  applyAudioProcessor,
  clearMicroProcessor,
  destroyWorkletNode,
  setupProcessingTrack,
} from "./audioProcessor";
import { isCurrentSession, needSync, sessionActive } from "./pipe";
import { log } from "../../utils/logger";
import { resumeAudioContext } from "../audioCtx";
import type { AudioProcessor } from "../../utils/AudioProcessor";
import type { MediaDesired } from "./state";

export async function tracksTask(desired: MediaDesired) {
  if (!sessionActive.value) return;
  const constraints: CreateLocalTracksOptions = {
    audio: false,
    video: false,
  };
  // Браузерные шумоподавление и эхоподавление включены ВСЕГДА, независимо от
  // тумблера AI-шумодава: он накладывается сверху уже в аудиографе и
  // constraints не меняет. voiceIsolation намеренно не указываем — ни false,
  // ни true: выбор остаётся за браузером (autoGainControl тоже не трогаем).
  const microConstraints = (): MediaTrackConstraints =>
    ({
      ...(desired.microDevice
        ? { deviceId: desired.microDevice }
        : {}),
      noiseSuppression: true,
      echoCancellation: true,
      autoGainControl: true,
    }) as MediaTrackConstraints;
  const needCameraTrack = (desired.cameraEnabled || desired.cameraOnSettings) && !cameraDeviceError.value
  //Камера
  if (cameraTrack.value === null && needCameraTrack) {
    if (desired.cameraDevice) {
      constraints.video = {
        deviceId: desired.cameraDevice,
      };
    } else {
      constraints.video = true;
    }
  } else if (cameraTrack.value && !needCameraTrack){
    cameraTrack.value.stop();
    cameraTrack.value = null;
    realCameraDevice.value = null;
  } else if (cameraTrack.value) {
    if (
      desired.cameraDevice &&
      desired.cameraDevice !== realCameraDevice.value
    ) {
      constraints.video = {
        deviceId: desired.cameraDevice,
      };
    }
  }

  //Микрофон, запрашиваем всегда если нету т.к. нужен для анализа
  // Constraints не зависят от тумблера шумодава, поэтому аудио запрашиваем
  // только когда трека нет или реальное устройство разошлось с желаемым.
  if (microTrack.value === null) {
    constraints.audio = microConstraints();
  } else if (
    desired.microDevice &&
    desired.microDevice !== realMicroDevice.value
  ) {
    constraints.audio = microConstraints();
  }

  const handleCameraDeviceMismatch = (
    track: LocalTrack,
    constraintsDeviceId: ConstrainDOMString | undefined,
  ) => {
    const cameraDevice = track.mediaStreamTrack.getSettings().deviceId!;
    realCameraDevice.value = cameraDevice;
    if (
      desired.cameraDevice &&
      desired.cameraDevice === constraintsDeviceId &&
      cameraDevice !== constraintsDeviceId
    ) {
      mediaActions.resetCameraDevice();
    }
    cameraDeviceError.value = null;
  };

  const handleMicroDeviceMismatch = (
    track: LocalTrack,
    constraintsDeviceId: ConstrainDOMString | undefined,
  ) => {
    const microDevice = track.mediaStreamTrack.getSettings().deviceId!;
    realMicroDevice.value = microDevice;
    if (
      desired.microDevice &&
      desired.microDevice === constraintsDeviceId &&
      microDevice !== constraintsDeviceId
    ) {
      mediaActions.resetMicroDevice();
    }
    microDeviceError.value = null;
  };

  const createTracks = async () => {
    const tracks = await createLocalTracks(constraints);
    if (!isCurrentSession(desired.generation)) {
      // cleanup прошёл, пока браузер открывал устройства: полученное сразу
      // гасим, ничего не подключая.
      for (const track of tracks) track.stop();
      return;
    }
    for (const track of tracks) {
      if (!isCurrentSession(desired.generation)) {
        // Сессия умерла во время обработки предыдущего трека: гасим весь батч.
        for (const rest of tracks) rest.stop();
        return;
      }
      if (track.kind === Track.Kind.Video) {
        const constraintsDeviceId =
          constraints.video && constraints.video !== true
            ? constraints.video.deviceId
            : undefined;
        handleCameraDeviceMismatch(track, constraintsDeviceId);
        await handleCameraTrack(track, desired);
      } else if (track.kind === Track.Kind.Audio) {
        const constraintsDeviceId =
          constraints.audio && constraints.audio !== true
            ? constraints.audio.deviceId
            : undefined;
        handleMicroDeviceMismatch(track, constraintsDeviceId);
        await handleMicroTrack(track, desired);
      }
    }
  };

  const createTracksSeparately = async () => {
    const attempts: Promise<void>[] = [];
    if (constraints.video) {
      attempts.push(
        createLocalVideoTrack(
          constraints.video === true ? undefined : constraints.video,
        )
          .then(async (track) => {
            if (!isCurrentSession(desired.generation)) {
              track.stop();
              return;
            }
            const constraintsDeviceId =
              constraints.video && constraints.video !== true
                ? constraints.video.deviceId
                : undefined;
            handleCameraDeviceMismatch(track, constraintsDeviceId);
            await handleCameraTrack(track, desired);
          })
          .catch((err) => {
            if (!isCurrentSession(desired.generation)) return;
            log.error('err create camera track', err)
            const error =
              MediaDeviceFailure.getFailure(err) || MediaDeviceFailure.Other;
            log.error('err create camera track', error)
            cameraDeviceError.value = error;
            mediaActions.cameraOff();
            mediaActions.resetCameraDevice();
          }),
      );
    }
    if (constraints.audio) {
      attempts.push(
        createLocalAudioTrack(
          constraints.audio === true ? undefined : constraints.audio,
        )
          .then(async (track) => {
            if (!isCurrentSession(desired.generation)) {
              track.stop();
              return;
            }
            const constraintsDeviceId =
              constraints.audio && constraints.audio !== true
                ? constraints.audio.deviceId
                : undefined;
            handleMicroDeviceMismatch(track, constraintsDeviceId);
            await handleMicroTrack(track, desired);
          })
          .catch((err) => {
            if (!isCurrentSession(desired.generation)) return;
            log.error('err create micro track', err)
            const error =
              MediaDeviceFailure.getFailure(err) || MediaDeviceFailure.Other;
            log.error('err create micro track', error)
            microDeviceError.value = error;
            mediaActions.microOff();
            mediaActions.resetMicroDevice();
          }),
      );
    }
    await Promise.all(attempts);
  };

  log.debug('createTracks', JSON.parse(JSON.stringify(constraints)))
  try {
    if (constraints.video && constraints.audio) {
      await createTracks();
    } else if (constraints.video || constraints.audio) {
      await createTracksSeparately();
    }
  } catch(err) {
    log.error('err createTracks', err)
    if (!isCurrentSession(desired.generation)) return;
    await createTracksSeparately();
  }
}

async function handleCameraTrack(track: LocalTrack, desired: MediaDesired) {
  log.debug('handleCameraTrack')
  if (!isCurrentSession(desired.generation)) {
    track.stop();
    return;
  }
  if (cameraTrack.value) {
    cameraTrack.value.stop();
  }
  await applyBlurProcessor(track, desired).catch((e) =>
    log.error("err blur processor", e),
  );
  // cleanup/новая сессия могли прийти, пока грузился фон.
  if (!isCurrentSession(desired.generation)) {
    track.stop();
    return;
  }
  cameraTrack.value = track;
  track.once("ended", () => {
    log.debug('ended cameraTrack')
    if (cameraTrack.value === track) {
      cameraTrack.value = null;
      realCameraDevice.value = null;
    }
    needSync();
  });
}

async function handleMicroTrack(
  track: LocalTrack,
  desired: MediaDesired,
) {
  log.debug('handleMicroTrack')
  if (!isCurrentSession(desired.generation)) {
    track.stop();
    return;
  }
  resumeAudioContext();
  if (microTrack.value) {
    const previous = microTrack.value;
    const previousProcessor = previous.getProcessor() as AudioProcessor;
    if (previousProcessor) {
      await previousProcessor.destroy().catch((e) =>
        log.error("err destroy previous micro processor", e),
      );
    }
    // cleanup/новая сессия могли прийти, пока уничтожался процессор: refs
    // больше не наши — останавливаем только собственный трек и выходим.
    if (!isCurrentSession(desired.generation)) {
      track.stop();
      return;
    }
    previous.stop();
  }
  if (microTrackForProcessing.value) {
    microTrackForProcessing.value.stop();
    destroyWorkletNode(microTrackForProcessing.value);
  }
  const processingTrack = track.mediaStreamTrack.clone();
  const a = setupProcessingTrack(processingTrack)
    .catch((e) => {
      log.error("err setup processing track", e);
    })
    .finally(() => {
      if (!isCurrentSession(desired.generation)) {
        // cleanup прошёл, пока настраивался VAD-граф: ничего не подключаем,
        // гасим и ворклет-узел, и клон.
        destroyWorkletNode(processingTrack);
        processingTrack.stop();
        return;
      }
      microTrackForProcessing.value = processingTrack;
      processingTrack.onended = () => {
        log.debug('ended processingTrack')
        destroyWorkletNode(processingTrack);
        if (microTrackForProcessing.value === processingTrack) {
          microTrackForProcessing.value = null;
        }
        if (microTrack.value === track) {
          track.stop();
          microTrack.value = null;
          realMicroDevice.value = null;
        }
        needSync();
      };
    });
  const b = applyAudioProcessor(track, desired.noiseSuppression)
    .catch((e) => log.error("err apply audio processor", e))
    .finally(() => {
      if (!isCurrentSession(desired.generation)) {
        // cleanup не знает про этот трек (он ещё не в refs) — гасим сами:
        // процессор, оба трека и VAD-worklet.
        const processor = track.getProcessor() as AudioProcessor | undefined;
        if (processor) void clearMicroProcessor(processor);
        destroyWorkletNode(processingTrack);
        track.stop();
        processingTrack.stop();
        return;
      }
      microTrack.value = track;
      track.once("ended", () => {
        log.debug('ended microTrack')
        clearMicroProcessor(track.getProcessor() as AudioProcessor);
        destroyWorkletNode(processingTrack);
        if (microTrack.value === track) {
          microTrack.value = null;
          realMicroDevice.value = null;
        }
        if (microTrackForProcessing.value === processingTrack) {
          processingTrack.stop();
          microTrackForProcessing.value = null;
        }
        needSync();
      });
    });
  await Promise.all([a, b]);
}
